// MUI
import { Grid } from "@mui/material";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSpecialUnitsManagedDataTableConfig } from "../hooks/useSpecialUnitsManagedDataTableConfig";

// Components
import RowFeedback from "@/Common/helpers/RowFeedback";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import { useManagedSpecialUnits } from "@/Motivators/context/SpecialUnits/ManagedContext";

// Interfaces
import {
	Cache,
	IOnUpdateManagedSpecialUnitsListResult,
	ISpecialUnitsManagedTableProps,
	Result,
	SpecialUnitsList,
	Unit,
} from "./interfaces";

export default function SpecialUnitsManagedTable(props: Readonly<ISpecialUnitsManagedTableProps>) {
	const { apiRef, locationId, motivatorType, listId } = props;
	const queryClient = useQueryClient();
	const { columns, groupingColDef } = useSpecialUnitsManagedDataTableConfig(apiRef, []);
	const { setSearchExist, setHidePanelResponse, setUpdateData } = useManagedSpecialUnits();
	// Controls the selected rows using a state.
	const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
	const { getSubscription } = useSubscriptions();
	const specialUnitListItemsSubscription = getSubscription([motivatorType + "Body", locationId, listId]);
	const inventorySuscriptions = getSubscription(["managedSpecialUnits", locationId, props.commissionCycleId]);

	const getUpdatedUnits = (currentCache: { units: Unit[] }) => {
		return currentCache?.units ? [...currentCache.units] : [];
	};

	specialUnitListItemsSubscription?.subscriptions?.onCreateItem?.setCallback((result: { data: { onCreateSpecialUnitsItem: Result } }) => {
		const response = result.data.onCreateSpecialUnitsItem;
		const items = response?.units || [];

		if (items.length) {
			queryClient.setQueryData(["specialUnitsSetupEntry", locationId, listId], (currentCache: { units: Unit[] }) => {
				const updated = getUpdatedUnits(currentCache);
				items.forEach((item) => {
					item.inventoryList.forEach((inventory) => {
						const childHierarchyToRemove = inventory.hierarchy[1];

						updated.forEach((cacheItem) => {
							cacheItem.inventoryList = cacheItem.inventoryList.filter((inventoryObj) => {
								return !inventoryObj.hierarchy.includes(childHierarchyToRemove);
							});
						});
					});
				});

				items.forEach((item) => {
					const present = updated.find((element) => element._id === item._id);
					if (present) {
						updated.forEach((updateItem, key) => {
							if (updateItem._id === item._id) {
								updated[key] = item;
							}
						});
					} else {
						updated.push(item);
					}
				});

				const filteredUnits = updated.filter((unit) => !(unit.criteria === "fixed" && unit.inventoryList.length === 0));
				checkSearchExist(props.query, filteredUnits);
				setUpdateData(0);
				return { ...currentCache, units: filteredUnits };
			});
		}
	});

	specialUnitListItemsSubscription?.subscriptions?.onDeleteItem?.setCallback((result: { data: { onDeleteSpecialUnitsItem: Result } }) => {
		const response = result.data.onDeleteSpecialUnitsItem;
		queryClient.setQueryData(["specialUnitsSetupEntry", locationId, listId], (currentCache: unknown) => {
			if (typeof currentCache === "object" && currentCache !== null && "units" in currentCache) {
				checkSearchExist(props.query, response?.units || []);
				setUpdateData(0);
				return { ...currentCache, units: response?.units || [] };
			}
			return currentCache;
		});
	});

	inventorySuscriptions?.subscriptions?.onUpdateManagedSpecialUnitsList?.setCallback((result: IOnUpdateManagedSpecialUnitsListResult) => {
		const dataResponse = result.data.onUpdateManagedSpecialUnitsList;
		const { transactionType, targetIds } = dataResponse;
		if (transactionType === "inserted") {
			dataResponse.specialUnitsLists?.forEach((specialUnitsList) => {
				const identifier = ["specialUnitsSetupEntry", dataResponse.locationId, specialUnitsList?._id];
				queryClient.setQueryData(identifier, (currentCache: Cache) => {
					if (typeof currentCache === "object" && currentCache !== null && "units" in currentCache) {
						const updated = mergeUnits(currentCache, specialUnitsList);
						return { ...currentCache, units: updated.units, lastModifiedOn: new Date().toISOString() };
					}
					return currentCache;
				});
			});
		} else if (transactionType === "deleted" && targetIds?.length) {
			const cacheKeys = queryClient
				.getQueryCache()
				.getAll()
				.map((query) => query.queryKey);
			cacheKeys.forEach((key) => {
				processCacheKey([...key], targetIds);
			});
		}
	});

	const updateCacheUnits = (currentCache: Cache, targetIds: string[]): Cache => {
		if (typeof currentCache === "object" && currentCache !== null && "units" in currentCache) {
			const updatedUnits = currentCache.units.map((unit) => {
				if (unit.criteria === "dynamic") {
					const updatedInventoryList = unit.inventoryList.filter((inventory) => !targetIds.includes(inventory._id));
					return { ...unit, inventoryList: updatedInventoryList };
				}
				return unit;
			});
			return { ...currentCache, units: updatedUnits, lastModifiedOn: new Date().toISOString() };
		}
		return currentCache;
	};

	const processCacheKey = (key: unknown[], targetIds: string[]): void => {
		if (Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith("specialUnitsSetupEntry")) {
			queryClient.setQueryData(key, (currentCache: Cache) => updateCacheUnits(currentCache, targetIds));
		}
	};

	const mergeUnits = (currentCache: Cache, specialUnitsList: SpecialUnitsList): Cache => {
		const currentUnitsMap = new Map(currentCache.units.map((unit) => [unit._id, unit]));
		if (Array.isArray(specialUnitsList.units)) {
			specialUnitsList.units.forEach((specialUnit) => {
				if (currentUnitsMap.has(specialUnit._id)) {
					const currentUnit = currentUnitsMap.get(specialUnit._id);
					if (currentUnit) {
						const mergedInventoryList = [...currentUnit.inventoryList, ...specialUnit.inventoryList].filter(
							(item) => "inventory" in item
						);

						currentUnit.inventoryList = Array.from(new Set(mergedInventoryList));
					}
				}
			});
		}

		return currentCache;
	};

	const checkSearchExist = (input: string, data: Unit[]): boolean => {
		const sortString = (str: string | undefined): string => str?.split("")?.sort()?.join("")?.trim()?.toLowerCase() ?? "";
		const searchValues = data?.map((item) => item.search).filter((search) => search !== undefined);
		const currentSearch = sortString(input);
		for (let i = 0; i < searchValues?.length; i++) {
			if (currentSearch === sortString(searchValues[i])) {
				setSearchExist(true);
				return true;
			}
		}

		setSearchExist(false);
		return false;
	};

	const handleDelete = async (ids: GridRowId[]) => {
		ids?.forEach((id: GridRowId) => {
			RowFeedback.skeleton(id, apiRef)?.delete()?.display();
		});

		props.deleteRows(ids);
	};

	const handleRowSelected = (newSelection: GridRowSelectionModel) => {
		setSelectedRows([...newSelection]);
		setHidePanelResponse(true);
	};

	if (!props.rows?.length) {
		return null;
	}

	return (
		<Grid className="managed-table">
			<DataSmartTable
				apiRef={apiRef}
				columns={columns}
				rows={props.rows ?? []}
				maxRows={17}
				enableCustomScrollbars
				columnVisibilityModel={{
					_id: false,
				}}
				slots={{
					toolbar: CommonToolbar,
				}}
				onRowSelectionModelChange={handleRowSelected}
				slotProps={{
					toolbar: {
						filter: {
							hide: false,
							disabled: false,
						},
						delete: {
							onDeleteAction: (ids: GridRowId[]) => {
								handleDelete(ids);
							},
							deleteTitle: "Remove Units",
							hide: !selectedRows.length,
						},
					},
				}}
				treeData
				checkboxSelection
				disableRowSelectionOnClick={true}
				getRowClassName={(params) =>
					!params.row.isParent ? "[&_.MuiDataGrid-cellCheckbox>span]:invisible [&_.MuiDataGrid-cellCheckbox]:!bg-[#FFF]" : ""
				}
				getTreeDataPath={(row) => row.hierarchy}
				groupingColDef={groupingColDef}
				emptyTableImageWidth={"100px"}
				customProps={{
					tableContainerStyle: { height: "100%", width: "100%", minHeight: "325px" },
					tableClassName: {
						"functional-table": false,
						"edit-table": true,
						"[&_.toolbar-actions]:!right-0": true,
						"!top-[-8px]": true,
						"[&_.MuiDataGrid-main]:!mt-[20px]": true,
					},
					expandableRows: true,
					hide: false,
				}}
			/>
		</Grid>
	);
}
